import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import ProjectSidebar from './ProjectSidebar';
import { useResponsive } from '../../hooks/useResponsive';
import usePortalUsageTracker from '../../hooks/usePortalUsageTracker';

/**
 * AppLayout - Shared layout component with sidebar
 * Used on both landing page (no project) and project pages
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render in main area
 * @param {string|null} props.projectId - Project ID or null if no project selected
 */
const AppLayout = ({ children, projectId = null, selectedDepartment = 'projects', onSelectDepartment, selectedEstimateId = null, selectedEstimateNumber = null, jobNumber = null }) => {
  const { isMobile, isTablet } = useResponsive();
  usePortalUsageTracker();
  
  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const location = useLocation();
  const [activeMenuItem, setActiveMenuItem] = useState('home');

  React.useEffect(() => {
    if (location.pathname.startsWith('/resource-management')) setActiveMenuItem('resources-management');
    else if (location.pathname.startsWith('/fabrication-histogram')) setActiveMenuItem('resources-fabrication');
    else if (location.pathname.startsWith('/portfolio-evm')) setActiveMenuItem('resources-portfolio-evm');
  }, [location.pathname]);
  
  // Save sidebar state to localStorage
  React.useEffect(() => {
    try {
      localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [sidebarCollapsed]);
  
  return (
    <div style={{ 
      display: 'flex', 
      height: 'calc(100vh - var(--header-height, 45px))',
      overflow: 'hidden'
    }}>
      {/* Mobile Menu Toggle Button */}
      {(isMobile || isTablet) && (
        <button
          onClick={() => setMobileSidebarOpen(true)}
          aria-label="Open menu"
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            boxShadow: '0 4px 12px rgba(220, 53, 69, 0.4)',
            cursor: 'pointer',
            zIndex: 90,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
          }}
        >
          ☰
        </button>
      )}

      {/* Left Sidebar */}
      <ProjectSidebar
        activeMenuItem={activeMenuItem}
        onMenuItemClick={setActiveMenuItem}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        isMobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
        projectId={projectId}
        selectedDepartment={selectedDepartment}
        onSelectDepartment={onSelectDepartment}
        selectedEstimateId={selectedEstimateId}
        selectedEstimateNumber={selectedEstimateNumber}
        jobNumber={jobNumber}
      />

      {/* Main Content Area */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto',
        scrollbarGutter: 'stable',
        backgroundColor: '#f5f7fa',
      }}>
        {children}
      </div>

    </div>
  );
};

export default AppLayout;

